import React, { useId, useState } from 'react';

import { useControllableState } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { TruncateText } from '@mint-lib/tw-components';
import {
  ComboboxGroup,
  ComboboxInput,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';

import { Loader } from './Loader.jsx';
import { VirtualizedYContainer } from './VirtualizedYContainer.jsx';

type ComboboxOption = {
  label: string;
  value: string;
};

type ComboboxProps<T = ComboboxOption> = {
  label?: string;
  placeholder?: string;
  infoIconWithTooltip?: string;
  inputValue?: string | null;
  defaultInputValue?: string | null;
  options: T[];
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  onChange: (value: T) => void;
  isLoading?: boolean;
  noOptionsText?: string;
  withSearch?: boolean;
  disabled?: boolean;
  className?: string;
  menuItemClassName?: string;
  size?: 'small' | 'medium';
  color?: 'default' | 'warning' | 'error';
  groupBy?: (option: T) => string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderOption?: (option: T) => React.ReactNode;
  inputProps?: React.ComponentProps<typeof ComboboxInput>['inputProps'];
  /** Forwarded unchanged to every option; the input names itself. */
  'data-dd-action-name'?: string;
};

export const Combobox = <T,>({
  className,
  label,
  infoIconWithTooltip,
  placeholder,
  inputValue: controlledInputValue,
  defaultInputValue,
  options,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionLabel = (v: any) => v,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getOptionValue = (v: any) => v,
  onChange,
  isLoading,
  noOptionsText,
  withSearch,
  disabled,
  groupBy,
  open: controlledOpen,
  onOpenChange,
  renderOption,
  menuItemClassName,
  size = 'medium',
  color,
  inputProps,
  'data-dd-action-name': optionActionName,
}: ComboboxProps<T>) => {
  const { t } = useTranslation('@myn/import-hub');
  const id = useId();

  const [isOpen, setIsOpen] = useControllableState({
    prop: controlledOpen,
    defaultProp: false,
    onChange: onOpenChange,
  });

  const [inputValue, setInputValue] = useControllableState({
    prop: controlledInputValue,
    defaultProp: defaultInputValue,
  });

  const handleChange = (optionValue: string) => {
    const option = options.find((opt) => getOptionValue(opt) === optionValue);
    if (option) {
      onChange(option);
      setInputValue(getOptionLabel(option));
      setIsOpen(false);
    }
  };

  return (
    <ComboboxInputRoot className={className}>
      {label && (
        <ComboboxInputLabel
          label={label}
          htmlFor={id}
          infoIconWithTooltip={infoIconWithTooltip}
        />
      )}
      <ComboboxInput
        id={id}
        value={inputValue ?? ''}
        placeholder={placeholder}
        popoverClassName="z-popover"
        open={isOpen}
        onOpenChange={setIsOpen}
        size={size}
        color={color}
        disabled={disabled}
        inputProps={inputProps}
      >
        <ComboboxPopover
          withSearch={withSearch}
          isLoading={isLoading}
          options={options}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          noOptionsText={noOptionsText ?? t('No options')}
          onChange={handleChange}
          groupBy={groupBy}
          renderOption={renderOption}
          menuItemClassName={menuItemClassName}
          inputValue={inputValue}
          optionActionName={optionActionName}
        />
      </ComboboxInput>
    </ComboboxInputRoot>
  );
};

type ComboboxPopoverProps<T> = {
  withSearch?: boolean;
  inputValue?: string | null;
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  noOptionsText: string;
  groupBy?: (option: T) => string;
  renderOption?: (option: T) => React.ReactNode;
  menuItemClassName?: string;
  optionActionName?: string;
};

const ComboboxPopover = <T,>(props: ComboboxPopoverProps<T>) => {
  return props.groupBy ? (
    <ComboboxPopoverWithGroupBy {...props} groupBy={props.groupBy} />
  ) : (
    <ComboboxPopoverVirtualized {...props} />
  );
};

const ComboboxPopoverVirtualized = <T,>({
  withSearch,
  options,
  getOptionLabel,
  getOptionValue,
  isLoading,
  noOptionsText,
  onChange,
  renderOption,
  menuItemClassName,
  inputValue,
  optionActionName,
}: Omit<ComboboxPopoverProps<T>, 'groupBy'>) => {
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(searchValue.toLowerCase()),
  );

  return (
    <ComboboxMenu
      shouldFilter={false}
      withSearch={withSearch}
      onValueChange={setSearchValue}
      emptyMessage={isLoading ? <Loader /> : noOptionsText}
    >
      <div>
        <VirtualizedYContainer className="max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto">
          {filteredOptions.map((option) => {
            const optionValue = getOptionValue(option);
            const isSelected = inputValue === getOptionLabel(option);
            return (
              <ComboboxItem
                key={optionValue}
                value={optionValue}
                onSelect={onChange}
                className={menuItemClassName}
                selected={isSelected}
                data-dd-action-name={optionActionName}
              >
                {renderOption ? (
                  renderOption(option)
                ) : (
                  <TruncateText>
                    <span className="block">{getOptionLabel(option)}</span>
                  </TruncateText>
                )}
              </ComboboxItem>
            );
          })}
        </VirtualizedYContainer>
      </div>
    </ComboboxMenu>
  );
};

const ComboboxPopoverWithGroupBy = <T,>({
  withSearch,
  options,
  getOptionLabel,
  getOptionValue,
  isLoading,
  noOptionsText,
  onChange,
  groupBy,
  renderOption,
  menuItemClassName,
  inputValue,
  optionActionName,
}: Omit<ComboboxPopoverProps<T>, 'groupBy'> & {
  groupBy: (option: T) => string;
}) => {
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(searchValue.toLowerCase()),
  );

  const groupedOptions = filteredOptions.reduce(
    (acc, option) => {
      const group = groupBy(option);
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(option);
      return acc;
    },
    {} as Record<string, T[]>,
  );

  return (
    <ComboboxMenu
      shouldFilter={false}
      withSearch={withSearch}
      onValueChange={setSearchValue}
      emptyMessage={isLoading ? <Loader /> : noOptionsText}
    >
      {Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
        <ComboboxGroup key={groupName} header={groupName}>
          {groupOptions.map((option) => {
            const optionValue = getOptionValue(option);
            const isSelected = inputValue === getOptionLabel(option);
            return (
              <ComboboxItem
                key={optionValue}
                value={optionValue}
                onSelect={onChange}
                className={menuItemClassName}
                selected={isSelected}
                data-dd-action-name={optionActionName}
              >
                {renderOption ? (
                  renderOption(option)
                ) : (
                  <TruncateText>
                    <span className="block">{getOptionLabel(option)}</span>
                  </TruncateText>
                )}
              </ComboboxItem>
            );
          })}
        </ComboboxGroup>
      ))}
    </ComboboxMenu>
  );
};
